import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";

// Lightweight per-page guided tour. A page registers an ordered list of steps
// (each: { selector, title, body }). The floating "?" button opens a prompt
// offering the tour; accepting spotlights each section one-by-one with an
// explanation. Only pages that register steps get a working "?" tour.
const TourContext = createContext(null);
export const useTour = () => useContext(TourContext);

export function TourProvider({ children }) {
    const [steps, setSteps] = useState([]);
    const [promptOpen, setPromptOpen] = useState(false);
    const [active, setActive] = useState(-1); // index into steps, -1 = inactive

    const registerSteps = useCallback((s) => setSteps(Array.isArray(s) ? s : []), []);
    const clearSteps = useCallback(() => { setSteps([]); setActive(-1); setPromptOpen(false); }, []);
    const openPrompt = useCallback(() => { if (steps.length) setPromptOpen(true); }, [steps]);
    // Ask for a tour of the NEXT page that registers one (My Account → back to the page).
    const [pending, setPending] = useState(false);
    const requestTour = useCallback(() => setPending(true), []);
    useEffect(() => { if (pending && steps.length) { setPromptOpen(true); setPending(false); } }, [pending, steps]);
    const startTour = useCallback(() => { setPromptOpen(false); setActive(0); }, []);
    const endTour = useCallback(() => setActive(-1), []);
    const next = useCallback(() => setActive((i) => (i + 1 < steps.length ? i + 1 : -1)), [steps.length]);
    const prev = useCallback(() => setActive((i) => Math.max(0, i - 1)), []);

    const value = { hasTour: steps.length > 0, steps, promptOpen, active, registerSteps, clearSteps, openPrompt, requestTour, startTour, endTour, next, prev, setPromptOpen };
    return (
        <TourContext.Provider value={value}>
            {children}
            <TourOverlay />
        </TourContext.Provider>
    );
}

// Registers steps for the lifetime of the calling page component.
export function usePageTour(steps) {
    const ctx = useTour();
    const reg = ctx?.registerSteps; const clr = ctx?.clearSteps;
    useEffect(() => {
        if (!reg) return;
        reg(steps);
        return () => clr && clr();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}

function TourOverlay() {
    const ctx = useTour();
    const [rect, setRect] = useState(null);
    const cardRef = useRef(null);
    const active = ctx?.active ?? -1;
    const step = active >= 0 ? ctx.steps[active] : null;

    useLayoutEffect(() => {
        if (!step) { setRect(null); return; }
        let raf;
        const measure = () => {
            const el = document.querySelector(step.selector);
            if (el) {
                el.scrollIntoView({ block: "center", behavior: "smooth" });
                const r = el.getBoundingClientRect();
                setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
            } else {
                setRect(null);
            }
        };
        measure();
        const t = setTimeout(measure, 350); // after smooth scroll settles
        const onWin = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(measure); };
        window.addEventListener("resize", onWin);
        window.addEventListener("scroll", onWin, true);
        return () => { clearTimeout(t); cancelAnimationFrame(raf); window.removeEventListener("resize", onWin); window.removeEventListener("scroll", onWin, true); };
    }, [step]);

    // Keyboard shortcuts while a tour step is showing:
    //   → / Enter / Space  → next step      ← / < / Backspace / Delete → previous
    //   Esc → end the tour.
    useEffect(() => {
        if (!step) return undefined;
        const onKey = (e) => {
            if (["ArrowRight", ">", ".", "Enter", " ", "Spacebar"].includes(e.key)) { e.preventDefault(); ctx.next(); }
            else if (["ArrowLeft", "<", ",", "Backspace", "Delete"].includes(e.key)) { e.preventDefault(); ctx.prev(); }
            else if (e.key === "Escape") { e.preventDefault(); ctx.endTour(); }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [step, ctx]);

    // Prompt card ("Would you like a tour?").
    if (ctx?.promptOpen) {
        return (
            <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4" data-testid="tour-prompt" onClick={() => ctx.setPromptOpen(false)}>
                <div className="w-full max-w-sm rounded-2xl border border-[#9D4CDD]/40 bg-[#11141F] p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                    <div className="text-xs font-bold uppercase tracking-wider text-[#9D4CDD] mb-1">Page tour</div>
                    <h3 className="text-xl font-extrabold text-white mb-2">Want a quick tour of this page?</h3>
                    <p className="text-sm text-white/60 mb-4">We&apos;ll walk you through each section and explain what everything means.</p>
                    <div className="mb-5 rounded-lg border border-white/10 bg-black/25 px-3 py-2.5" data-testid="tour-keybinds">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-[#9D4CDD] mb-1.5">Keyboard shortcuts</div>
                        <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-[11px] text-white/70">
                            <span className="inline-flex items-center gap-1"><kbd className="rounded bg-white/10 border border-white/15 px-1.5 py-0.5 font-mono text-[10px] text-white">T</kbd> Open tour</span>
                            <span className="inline-flex items-center gap-1"><kbd className="rounded bg-white/10 border border-white/15 px-1.5 py-0.5 font-mono text-[10px] text-white">→</kbd> Next</span>
                            <span className="inline-flex items-center gap-1"><kbd className="rounded bg-white/10 border border-white/15 px-1.5 py-0.5 font-mono text-[10px] text-white">←</kbd> Back</span>
                            <span className="inline-flex items-center gap-1"><kbd className="rounded bg-white/10 border border-white/15 px-1.5 py-0.5 font-mono text-[10px] text-white">Esc</kbd> Close</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={ctx.startTour} data-testid="tour-start" className="flex-1 px-4 py-2.5 rounded-lg bg-[#9D4CDD] hover:bg-[#B366F0] text-white font-bold uppercase tracking-wider text-xs">Start tour</button>
                        <button onClick={() => ctx.setPromptOpen(false)} data-testid="tour-dismiss" className="px-4 py-2.5 rounded-lg border border-white/15 text-white/60 hover:text-white font-bold uppercase tracking-wider text-xs">Not now</button>
                    </div>
                </div>
            </div>
        );
    }

    if (!step) return null;
    const pad = 8;
    const spot = rect ? { top: Math.max(0, rect.top - pad), left: Math.max(0, rect.left - pad), width: rect.width + pad * 2, height: rect.height + pad * 2 } : null;
    // Place the tooltip below the target if room, else above.
    const vh = typeof window !== "undefined" ? window.innerHeight : 800;
    const below = !rect || (rect.top + rect.height + 190 < vh);
    const cardTop = rect ? (below ? rect.top + rect.height + pad + 12 : Math.max(12, rect.top - pad - 200)) : vh / 2 - 100;
    const cardLeft = rect ? Math.min(Math.max(12, rect.left), (typeof window !== "undefined" ? window.innerWidth : 1200) - 360) : 40;

    return (
        <div className="fixed inset-0 z-[120]" data-testid="tour-overlay">
            {/* Spotlight cut-out via a huge box-shadow around the target. */}
            {spot ? (
                <div className="absolute rounded-xl pointer-events-none transition-all duration-300"
                    style={{ top: spot.top, left: spot.left, width: spot.width, height: spot.height, boxShadow: "0 0 0 9999px rgba(0,0,0,0.74)", border: "2px solid #9D4CDD", borderRadius: 14 }} />
            ) : (
                <div className="absolute inset-0 bg-black/74" />
            )}
            <div ref={cardRef} className="absolute w-[340px] max-w-[90vw] rounded-2xl border border-[#9D4CDD]/50 bg-[#11141F] p-4 shadow-2xl" style={{ top: cardTop, left: cardLeft }} data-testid="tour-step-card">
                <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#9D4CDD]">Step {active + 1} / {ctx.steps.length}</span>
                    <button onClick={ctx.endTour} data-testid="tour-close" className="text-white/40 hover:text-white text-sm leading-none">✕</button>
                </div>
                <h4 className="text-base font-extrabold text-white mb-1.5" data-testid="tour-step-title">{step.title}</h4>
                <p className="text-sm text-white/65 leading-relaxed mb-4" data-testid="tour-step-body">{step.body}</p>
                <div className="flex items-center justify-between gap-2">
                    <button onClick={ctx.prev} disabled={active === 0} data-testid="tour-prev" className="px-3 py-2 rounded-lg border border-white/15 text-white/60 hover:text-white disabled:opacity-30 font-bold uppercase tracking-wider text-[11px]">Back</button>
                    <div className="flex gap-1">
                        {ctx.steps.map((_, i) => <span key={i} className={`h-1.5 w-1.5 rounded-full ${i === active ? "bg-[#9D4CDD]" : "bg-white/20"}`} />)}
                    </div>
                    <button onClick={ctx.next} data-testid="tour-next" className="px-3 py-2 rounded-lg bg-[#9D4CDD] hover:bg-[#B366F0] text-white font-bold uppercase tracking-wider text-[11px]">{active + 1 === ctx.steps.length ? "Finish" : "Next"}</button>
                </div>
            </div>
        </div>
    );
}
